import {Component, OnDestroy, OnInit} from '@angular/core';
import {IotService} from "../../../services/iot.service";
import {Room, RoomService} from "../../../services/room.service";
import {ActivatedRoute} from "@angular/router";
import {environment} from "../../../../environments/environment";
import {firstValueFrom} from "rxjs";
import {Group, GroupService} from "../../../services/group.service";

import {ShowUserComponent} from "../../../dialogs/show-user/show-user.component";
import {MatDialog} from "@angular/material/dialog";
import {Question, Quiz, QuizResponse, QuizService} from "../../../services/quiz.service";
import {SimpleUser} from "../../../services/user.service";
import {Attendee} from "../../../services/attendee.service";
import {ClassTask, ClassTaskResponse, ClassTaskService} from "../../../services/class-task.service";
import {
  TaskDecision,
  VolunteerTask,
  VolunteerTaskResponse,
  VolunteerTaskService
} from "../../../services/volunteer-task.service";
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { MatButtonModule } from '@angular/material/button';
import { ClasstaskComponent } from '../../../components/classtask/classtask.component';
import { ScoreComponent } from '../../../components/score/score.component';
import { HudComponent } from '../../../components/hud/hud.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {NgIf, NgFor, JsonPipe} from '@angular/common';
import { Hub } from 'aws-amplify/utils';
import { CONNECTION_STATE_CHANGE } from '@aws-amplify/pubsub';
import { EventDataMap, HubPayload, StopListenerCallback } from '@aws-amplify/core/dist/esm/Hub/types';
import { AlertComponent } from "../../../components/alert/alert.component";
import { setThrowInvalidWriteToSignalError } from '@angular/core/primitives/signals';

@Component({
    selector: 'app-attend-room',
    templateUrl: './attend-room.component.html',
    styleUrls: ['./attend-room.component.scss'],
    standalone: true,
    imports: [NgIf, FaIconComponent, HudComponent, ScoreComponent, ClasstaskComponent, NgFor, MatButtonModule, AvatarComponent, JsonPipe, AlertComponent]
})
export class AttendRoomComponent implements OnInit, OnDestroy {

  loading: boolean = true ;

  roomId?: string ;
  room?: Room ;
  attendee?: Attendee ;

  volunteerTask?: VolunteerTask ;
  volunteerTaskResponse?: VolunteerTaskResponse ;

  classTask?: ClassTask ;
  classTaskResponse?: ClassTaskResponse ;

  quiz?: Quiz ;
  quizResponse?: QuizResponse ;

  group?: Group ;

  stopPubsubListener?: StopListenerCallback  ;

  error?: any ;
  connected: boolean = false;
  firstConnectionCompleted = false ;


  constructor(
    private rooms: RoomService,
    private volunteerTasks: VolunteerTaskService,
    private classTasks: ClassTaskService,
    public quizzes: QuizService,
    private groups: GroupService,
    private route: ActivatedRoute,
    public iot: IotService,
    public dialog: MatDialog
  ) { }

  get userId(): string | undefined {
    if (!this.attendee)
      return  ;

    return this.attendee.user.id ;
  }

  get groupId(): string | undefined {
    if (!this.group)
      return ;

    return this.group.id ;
  }

  get roomUrl():string | undefined {

    if (!this.room)
      return ;

    return `${environment.uiHost}/rooms/${this.room.id}`;

  }

  get isChosen(): boolean {

    if (!this.volunteerTask || !this.volunteerTask.chosenUserId) {
      return false ;
    }

    return this.volunteerTask.chosenUserId == this.userId ;
  }

  get isGroupChosen(): boolean {

    if (!this.volunteerTask || !this.volunteerTask.chosenGroupId) {
      return false ;
    }

    return this.volunteerTask.chosenGroupId == this.groupId ;
  }

  get hasActiveVolunteerTask(): boolean {
    if (!this.volunteerTask)
      return false ;

    return this.volunteerTask.status != "COMPLETED" && this.volunteerTask.status != "CANCELLED" ;
  }

  get optInClass(): string {

    if (this.volunteerTaskResponse?.decision == 'OPT_IN') {
      return 'opt-in' ;
    } else if (this.volunteerTaskResponse?.decision == 'OPT_OUT') {
      return 'deselected' ;
    } else {
      return 'inactive';
    }

  }

  get optOutClass(): string {

    if (this.volunteerTaskResponse?.decision == 'OPT_OUT') {
      return 'opt-out' ;
    } else if (this.volunteerTaskResponse?.decision == 'OPT_IN') {
      return 'deselected' ;
    } else {
      return 'inactive';
    }
  }


  get hasActiveClassTask(): boolean {
    if (!this.classTask)
      return false ;

    return this.classTask.status != "COMPLETED" && this.classTask.status != "CANCELLED" && this.classTask.status != "PAUSED" ;
  }



  get hasActiveQuiz(): boolean {
    if (!this.quiz)
      return false ;

    return this.quiz.status != "COMPLETED" && this.quiz.status != "CANCELLED" ;
  }

  public get currentQuestion(): Question | undefined {

    if (!this.quiz || this.quiz.status != "ACTIVE")
      return undefined ;

    if (this.quiz.currQuestionIndex == undefined)
      return undefined ;

    return this.quiz.questions[this.quiz.currQuestionIndex] ;
  }

  public get correctChoiceIndex(): number | undefined {

    if (!this.currentQuestion)
      return undefined ;

    let correctIndex: number | undefined = undefined ;
    this.currentQuestion.choices.forEach((choice, index) => {
      if (choice.correct) {
        correctIndex = index ;
      }
    });

    return correctIndex ;
  }

  get currentQuestionChoice(): number | undefined {

    if (!this.quizResponse || !this.quizResponse.answers)
      return ;

    if (!this.quiz || this.quiz.currQuestionIndex == undefined)
      return ;

    return this.quizResponse.answers[this.quiz.currQuestionIndex]?.choiceIndex ;
  }

  getChoiceStyle(choiceIndex: number) {

    if (this.currentQuestionChoice == choiceIndex) {
      return {
        backgroundColor: this.quizzes.getColor(choiceIndex),
        color: '#0f172a'
      }
    } else {
      return {
        border: "2px solid " + this.quizzes.getColor(choiceIndex),
        color: this.quizzes.getColor(choiceIndex)
      }
    }
  }

  async ngOnInit() {

    this.roomId = this.route.snapshot.params['roomId'] ;
    this.attendee = await firstValueFrom(this.rooms.joinRoom(this.roomId!)) ;

    await this.load() ;

    this.stopPubsubListener = Hub.listen('pubsub', (data) => {
          const { payload } = data;
          if (payload.event === CONNECTION_STATE_CHANGE) {
            this.handleConnectionStateChanged(payload);
          }
        });

    try {
      this.iot.getClient().subscribe({ topics: [`rooms/${this.roomId}`, `rooms/${this.roomId}/attendees/${this.attendee!.user.id}`] }).subscribe({
        next: (data) => this.handleMessage(data),
        error: (error) => console.error(error),
        complete: () => console.log('Done')
      });
    } catch(e) {
      console.log("Could not subscribe to topics", e) ;
      this.error = e ;
    }
  }

  async handleConnectionStateChanged(payload: HubPayload<EventDataMap>) {
    console.log('connection state changed', payload);

    const data = payload.data as { connectionState: string };
    if (data.connectionState == 'Connected') {
      this.connected = true ;
      if (!this.firstConnectionCompleted) {
        this.firstConnectionCompleted = true ;
      } else {
        await this.load();
      }
    } else {
      this.connected = false ;
    }
  }

  ngOnDestroy(): void {
      if (this.stopPubsubListener) {
        this.stopPubsubListener();
      }
  }

  async load() {

    if (!this.roomId) {
      return ;
    }


    try {
      this.loading = true ;

      this.room = await firstValueFrom(this.rooms.getRoom(this.roomId)) ;
      console.log(this.room);

      this.attendee = await firstValueFrom(this.rooms.joinRoom(this.roomId)) ;

      this.group = await firstValueFrom(this.groups.getMyGroup(this.roomId)) ;

      this.volunteerTask = await firstValueFrom(this.volunteerTasks.getCurrent(this.roomId));
      if (this.volunteerTask) {
        this.volunteerTaskResponse = await firstValueFrom(this.volunteerTasks.getMyResponse(this.volunteerTask.id!)) ;
      }

      this.classTask = await firstValueFrom(this.classTasks.getCurrent(this.roomId)) ;
      if (this.classTask) {
        this.classTaskResponse = await firstValueFrom(this.classTasks.getMyProgress(this.classTask.id!)) ;
      }

      this.quiz = await firstValueFrom(this.quizzes.getCurrentQuiz(this.roomId));
      if (this.quiz) {
        this.quizResponse = await firstValueFrom(this.quizzes.getMyResponse(this.quiz.id!));
      }
    } catch (e) {
      this.error = e ;
    }

    this.loading = false ;
  }

  async setVolunteerTaskDecision(decision: TaskDecision) {

    if (!this.volunteerTask) {
      return ;
    }

    try {
      this.volunteerTaskResponse = await firstValueFrom(
        this.volunteerTasks.updateMyResponse(this.volunteerTask.id!, decision)
      );
    }
    catch (e) {
      console.log(e);
    }

  }

  private handleMessage(message: any) {

    console.log("handling message", message);

    switch (message.messageType) {

      case 'Room':
        this.room = message.data as Room ;
        break ;
      case 'Attendee':
        this.attendee =  message.data as Attendee ;
        break ;
      case 'Group':
        this.handleGroupUpdate(message.data as Group) ;
        break ;
      case 'Quiz': 
        this.handleQuizUpdate(message.data as Quiz);
        break;
      case 'ClassTask':
        this.handleClassTaskUpdate(message.data as ClassTask);
        break;
      case 'VolunteerTask':
        this.handleVolunteerTaskUpdate(message.data as VolunteerTask);
        break;
      case 'QuizResponse': 
        this.handleQuizResponseUpdate(message.data as QuizResponse) ;
        break;
      case 'ClassTaskResponse':
        this.handleClassTaskResponseUpdate(message.data as ClassTaskResponse) ;
        break;
      case 'VolunteerTaskResponse':
        this.handleVolunteerTaskResponseUpdate(message.data as VolunteerTaskResponse);
        break;
      default:
        console.log("Did not handle message", message) ;
    }
  }

  handleGroupUpdate(group: Group) {
    let myGroup = false ;
    group.members.forEach(member => {
      if (member.id == this.attendee!.id.userId) {
        myGroup = true ;
      }
    }) ;

    if (myGroup) {
      this.group = group ;
    } else if (this.group?.id == group.id) {
      //user has been removed from this group
      this.group = undefined ;
    }
  }

  handleClassTaskUpdate(task: ClassTask) {

    const isChanged = this.classTask?.id != task.id ;

    this.classTask = task ;
    if (isChanged) {
      this.classTaskResponse = undefined ;
    }
  }

  handleClassTaskResponseUpdate(response: ClassTaskResponse) {

    console.log("Class Task Response: " + response) ;

    let isMine = response.userIds!.includes(this.attendee!.id.userId) ;
    
    if (isMine && !response.deleted) {
      this.classTaskResponse = response;
    } else if (this.classTaskResponse?.id == response.id) {
      this.classTaskResponse = undefined ;
    }
  }

  handleVolunteerTaskUpdate(task: VolunteerTask) {

    const isChanged = this.volunteerTask?.id != task.id ;

    this.volunteerTask = task ;
    if (isChanged) {
      this.volunteerTaskResponse = undefined ;
    }
  }

  handleVolunteerTaskResponseUpdate(response: VolunteerTaskResponse) {

    let isMine = response.userIds!.includes(this.attendee!.id.userId) ;
    
    if (isMine && !response.deleted) {
      this.volunteerTaskResponse = response;
    } else if (this.volunteerTaskResponse?.id == response.id) {
      this.volunteerTaskResponse = undefined ;
    }
  }

  handleQuizUpdate(quiz: Quiz) {
    const isChanged = this.quiz?.id != quiz.id ;

    this.quiz = quiz ;
    if (isChanged) {
      this.quizResponse = undefined ;
    }
  }

  handleQuizResponseUpdate(response: QuizResponse) {

    let isMine = response.userIds!.includes(this.attendee!.id.userId) ;
    
    if (isMine && !response.deleted) {
      this.quizResponse = response;
    } else if (this.quizResponse?.id == response.id) {
      this.quizResponse = undefined ;
    }
  }

  showUser(user:SimpleUser) {

    this.dialog.open(ShowUserComponent, {
      data: { user: user },
      panelClass: 'transparent-dialog'
    });
  }

  async choose(choiceIndex: number) {
    this.quizResponse = await firstValueFrom(this.quizzes.updateMyResponse(this.quiz!.id!, this.quiz!.currQuestionIndex!, choiceIndex));
  }
}
